import { personalUrl } from "utils/env";
import { getSubDomain } from "utils/helperFunctions";

export const fetchIdpInfo = async (emailAddress) => {
  try {
    const response = await fetch(`${personalUrl}identityProvider`, {
      method: "post",
      body: JSON.stringify({
        emailAddress,
      }),
    });
    if (response.ok) {
      const idpInfo = await response.json();
      return idpInfo;
    }
  } catch (error) {
    return error;
  }
  return false;
};

// Constants for Calendly URLs
const CALENDLY_BASE_URL = "https://calendly.com";
const PLATFORM_DISCUSSION_EVENT = "/criteria-platform-discussion";
const CALENDLY_EVENTS = {
  SMALL_BUSINESS: "/d/cq7j-tyk-nsk/criteria-platform-discussion-sb",
  MID_MARKET: "/d/cntx-ty9-fx2/criteria-platform-discussion-mm",
  ENTERPRISE: "/d/cnz3-5n8-7yb/criteria-platform-discussion-enterprise",
};
const AU_DEFAULT_REP = "criteria-ashley-bramble";

/**
 * Generates appropriate Calendly scheduling URL based on user type and company size
 * @param {boolean} isFreeTrial - Whether user is on free trial
 * @param {Object} rep - Rep information containing CSM and SDR details
 * @param {Object} utmParams - Marketing tracking parameters
 * @param {string} numberOfEmployees - Company size range (e.g. "0-100")
 * @returns {string} Formatted Calendly URL
 */
export const scheduleDemoManageMenu = (
  isFreeTrial,
  rep,
  numberOfEmployees,
  utmParams = {}
) => {
  // Handle missing or incomplete parameters
  const actualRep = rep || { sdr: { firstName: "sales", lastName: "team" } };

  // Format UTM parameters
  const { content = "", campaign = "", source = "", medium = "" } = utmParams;
  const utmParametersLink = `?utm_content=${content}&utm_campaign=${campaign}&utm_source=${source}&utm_medium=${medium}`;

  // Check region
  const isAU = getSubDomain() === "app-au";

  // Case 1: Non-free trial with assigned CSM
  if (!isFreeTrial && actualRep?.csm?.firstName && actualRep?.csm?.lastName) {
    const csmFirstName = actualRep.csm.firstName
      .replace(/[']/g, "")
      .toLowerCase();
    const csmLastName = actualRep.csm.lastName
      .replace(/[']/g, "")
      .toLowerCase();
    return `${CALENDLY_BASE_URL}/criteria-${csmFirstName}-${csmLastName}${utmParametersLink}`;
  }

  // Format SDR names (or use defaults if missing)
  const firstName = actualRep?.sdr?.firstName
    ? actualRep.sdr.firstName.replace(/[^a-zA-Z]/g, "").toLowerCase()
    : "sales";
  const lastName = actualRep?.sdr?.lastName
    ? actualRep.sdr.lastName.replace(/[^a-zA-Z-]/g, "").toLowerCase()
    : "team";

  // Case 2: Free trial with generic "sales team" rep
  if (isFreeTrial && firstName === "sales" && lastName === "team") {
    // For non-AU with employee count, use size-specific links
    if (!isAU && numberOfEmployees) {
      return getEmployeeSizeBasedUrl(numberOfEmployees, utmParametersLink);
    }

    // For AU or missing employee count
    const repName = isAU ? AU_DEFAULT_REP : `criteria-${firstName}-${lastName}`;
    return `${CALENDLY_BASE_URL}/${repName}${PLATFORM_DISCUSSION_EVENT}${utmParametersLink}`;
  }

  // Case 3: Default case (specific SDR)
  return `${CALENDLY_BASE_URL}/criteria-${firstName}-${lastName}${PLATFORM_DISCUSSION_EVENT}${utmParametersLink}`;
};

/**
 * Helper function to get URL based on company size
 * @param {string} employeeCount - Number of employees string (e.g. "0-100")
 * @param {string} utmParams - Formatted UTM parameter string
 * @returns {string} Appropriate Calendly URL
 */
const getEmployeeSizeBasedUrl = (employeeCount, utmParams) => {
  if (employeeCount?.includes("-")) {
    const [minEmployees, maxEmployees] = employeeCount.split("-").map(Number);

    if (minEmployees >= 0 && maxEmployees <= 250) {
      return `${CALENDLY_BASE_URL}${CALENDLY_EVENTS.SMALL_BUSINESS}${utmParams}`;
    }

    if (minEmployees >= 251 && maxEmployees <= 1000) {
      return `${CALENDLY_BASE_URL}${CALENDLY_EVENTS.MID_MARKET}${utmParams}`;
    }
  }

  // Default to enterprise for any other case
  return `${CALENDLY_BASE_URL}${CALENDLY_EVENTS.ENTERPRISE}${utmParams}`;
};
